import { ReactNode, useCallback, memo, useEffect, useMemo } from 'react';
import { Spinner, Box, BoxProps } from '@chakra-ui/react';
import {
  GoogleMap,
  useJsApiLoader,
  GoogleMapProps,
  Polygon,
} from '@react-google-maps/api';
import { useCurrentlySelectedCities } from 'state/currentLocation';
import { useMapCenter, useMapState, useMapZoom, useZoneOverlayPolygons } from 'state/map';
import { useZones } from 'state/projectTypes';
import { ParentItem } from 'types/misc';

type MapRef = google.maps.Map | null;

export type MapProps = BoxProps & {
  center?: GoogleMapProps['center'];
  zoom?: number;
  children?: ReactNode | ((map: MapRef) => ReactNode);
  mapProps?: Omit<
    GoogleMapProps,
    'bootstrapURLKeys' | 'defaultZoom' | 'defaultCenter' | 'onGoogleApiLoaded'
  >;
  sidePanel?: boolean;
  mini?: boolean;
  showPolygons?: boolean;
  showLoadingSpinner?: boolean;
};

const MapComponent = ({
  children,
  mapProps,
  sidePanel,
  mini,
  showPolygons = true,
  showLoadingSpinner = true,
  ...props
}: MapProps) => {
  const { updateMapLoaded, pinsLoading, updateMapRef } = useMapState();
  const { currentlySelectedCitiesIds } = useCurrentlySelectedCities();
  const { getZonesForCity } = useZones(currentlySelectedCitiesIds.join(','));
  const { selectedPolygons } = useMapState();
  const [zoneOverlayPolygons] = useZoneOverlayPolygons();

  useEffect(() => {
    return () => {
      updateMapRef(null);
    };
  }, []);

  const memoizedPolygons = useCallback(
    (zoneName: string) =>
      Object.keys(selectedPolygons)
        .map((name) => name.toUpperCase())
        .includes(zoneName.toUpperCase()),
    [selectedPolygons],
  );

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  // memo-ize getZonesForCity so it doesn't re-run on every render
  const zonesForCityMemo = useMemo(() => getZonesForCity, [getZonesForCity]);

  const flattenedPolygons = useCallback(
    (zoneName: string) => {
      return zoneOverlayPolygons[zoneName].flat();
    },
    [zoneOverlayPolygons],
  );

  const [mapCenter] = useMapCenter();
  const [mapZoom] = useMapZoom();

  // todo: we need to get rid of this stuff.
  if (loadError) return <>Error</>;
  if (!isLoaded) return <>Loading...</>;

  const parentBox = {
    height: '100%',
    position: 'relative',
    ...(sidePanel && {
      height: 'calc(100vh - 100px)',
      width: '50%',
    }),
    ...(mini && {
      height: 500,
      width: 500,
    }),
  };

  const loadingContainer = {
    position: 'absolute',
    display: pinsLoading && showLoadingSpinner ? 'flex' : 'none',
    top: '50%',
    right: 0,
    left: '50%',
  };

  const spinnerStyle = { width: '100px', height: '100px' };

  return (
    <Box sx={parentBox} {...props} id="portal-root">
      {isLoaded && (
        <>
          <GoogleMap
            onLoad={(map) => {
              updateMapRef(map);
              updateMapLoaded(true);
            }}
            clickableIcons={false}
            mapContainerStyle={{
              height: '100%',
              width: '100%',
            }}
            center={mapCenter}
            zoom={mapZoom}
            {...mapProps}
          >
            {isLoaded && (
              <>
                {children}
                {showPolygons &&
                // Polygons
                Object.keys(selectedPolygons).length > 0
                  ? zonesForCityMemo.map((zone: ParentItem) => {
                      if (selectedPolygons[zone.name]) {
                        // how to get Polygon on mapRef
                        if (!zoneOverlayPolygons[zone.name]) return null;
                        // only show polygons that are in the selectedPolygons
                        if (!selectedPolygons[zone.name]) return null;

                        const polygonColor = selectedPolygons[zone.name];

                        return (
                          <Polygon
                            key={`${zone.name}`}
                            paths={
                              zoneOverlayPolygons[zone.name] ??
                              flattenedPolygons(zone.name)
                            }
                            visible={memoizedPolygons(zone.name)}
                            options={{
                              strokeColor: polygonColor,
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                              fillColor: polygonColor,
                              fillOpacity: 0.35,
                              zIndex: 99,
                              clickable: false,
                              draggable: false,
                              editable: false,
                            }}
                          />
                        );
                      }

                      return null;
                    })
                  : null}
              </>
            )}
          </GoogleMap>
          <Box sx={loadingContainer}>
            <Spinner sx={spinnerStyle} color="#027a3b" size="xl" />
          </Box>
        </>
      )}
    </Box>
  );
};

export const Map = memo(MapComponent);
